<template>
  <div v-if="props.showError">
      <Transition
        enter-active-class="animate__animated animate__fadeInRight"
        leave-active-class="animate__animated animate__fadeOutRight"
        :duration="400"
      >
        <!-- Same idea as the success toast's forest-gradient — a dark, rich
             fill instead of a flat red block, so it stays in the same family
             (and looks identical regardless of light/dark mode) while still
             reading unmistakably as an error via the deep red tone. -->
        <div class="fixed z-[9999] top-4 right-4 left-4 sm:left-auto sm:w-96 rounded-2xl shadow-hero overflow-hidden error-gradient grain">

          <div class="px-4 py-3.5 flex items-start gap-3">
            <!-- error icon -->
            <div class="shrink-0 mt-0.5 h-8 w-8 rounded-full flex items-center justify-center bg-white/15 ring-4 ring-white/5">
              <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
            </div>

            <div class="flex-1 min-w-0 pt-0.5">
              <p class="text-sm font-semibold text-white">
                <slot name="title">Error</slot>
              </p>
              <p class="mt-0.5 text-xs text-white/70 font-medium leading-relaxed">
                <slot>Error occurred while processing your request.</slot>
              </p>
            </div>

            <button
              @click="$emit('closeError')"
              class="shrink-0 text-white/60 hover:text-white text-xl leading-none mt-0.5 transition-smooth"
            >&times;</button>
          </div>

          <!-- progress bar -->
          <div class="h-0.5 bg-white/15">
            <div class="h-full bg-white animate-shrink"></div>
          </div>
        </div>
      </Transition>
  </div>
</template>

<script lang="ts" setup>
import { onMounted } from "vue";

const props = defineProps<{ showError: boolean }>();

const emit = defineEmits<{
  (event: "closeError"): void;
}>();

onMounted(() => {
  // Errors get more time on screen than a success confirmation — worth
  // actually reading, not just glancing at.
  setTimeout(() => {
    emit("closeError");
  }, 4500);
});
</script>

<style scoped>
/* A radial "spotlight" gradient (fine on a huge hero) concentrates its
   brightest point in the middle and fades hard toward the edges — on a
   card this small that reads as an uneven faded blotch rather than a
   clean fill. A gentle diagonal linear gradient between two close, both
   clearly-red shades avoids that entirely. */
.error-gradient {
  background-image: linear-gradient(135deg, #E23A54 0%, #A21F3B 100%);
}

/* .grain also sets background-image (the noise texture) — without this
   combined rule, whichever of the two classes wins the cascade would wipe
   out the other's layer entirely instead of the two compositing together. */
.error-gradient.grain {
  background-image:
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='250' height='250'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='250' height='250' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E"),
    linear-gradient(135deg, #E23A54 0%, #A21F3B 100%);
  background-size: 250px 250px, 100% 100%;
  background-repeat: repeat, no-repeat;
}

@keyframes shrink {
  from { width: 100%; }
  to   { width: 0%; }
}
@-webkit-keyframes shrink {
  from { width: 100%; }
  to   { width: 0%; }
}
.animate-shrink {
  -webkit-animation: shrink 4.5s linear forwards;
  animation: shrink 4.5s linear forwards;
}
</style>
